import React, { useState, useMemo } from "react";
import "./KioskSimulator.css";

interface Highlight {
  id?: number;
  name?: string;
}

interface StepItem {
  id?: number;
  name?: string;
  image?: string;
  price?: number | null;
  calories?: number;
  calorieRange?: string;
  outOfStock?: boolean;
  itemBlock?: boolean;
  quantity?: number;
  portion?: string;
  menuGUID?: string | null;
  menuGuid?: string | null;
  recipeGUID?: string | null;
  recipeGuid?: string | null;
  selected?: boolean;
  highlights?: Highlight[];
}

interface Step {
  id?: number;
  name?: string;
  required?: boolean;
  minSelect?: number;
  maxSelect?: number;
  signatureItem?: boolean;
  modifier?: string | null;
  items?: StepItem[];
  error?: boolean;
  isOnSale?: boolean;
}

interface Item {
  id?: number;
  name?: string;
  image?: string | null;
  price?: number | null;
  calories?: number;
  calorieRange?: string;
  outOfStock?: boolean;
  itemBlock?: boolean;
  quantity?: number;
  menuGUID?: string | null;
  menuGuid?: string | null;
  signatureItem?: boolean;
  heroItem?: boolean;
  bannerImage?: string | null;
  heroBadgeText?: string | null;
  heroBadgeStyle?: string | null;
  items: Item[];
  steps: Step[];
  addOns: Step[];
  highlights?: Highlight[];
}

interface PageVersion {
  id?: number;
  title?: string;
  image?: string;
  storeNumber?: number;
  selected?: boolean;
  items?: Item[];
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  customizations: string[];
  image: string | null;
}

let cartIdCounter = 0;
const nextCartId = () => `cart-${++cartIdCounter}`;

interface KioskSimulatorProps {
  menuData: PageVersion[];
}

const KioskSimulator: React.FC<KioskSimulatorProps> = ({ menuData }) => {
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [navStack, setNavStack] = useState<{ name: string; items: Item[] }[]>([]);
  const [viewingItem, setViewingItem] = useState<Item | null>(null);
  // Deep copy of viewing item's steps for interactive selection
  const [editableSteps, setEditableSteps] = useState<Step[]>([]);
  // Cart state
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [addedFlash, setAddedFlash] = useState<string | null>(null);
  // AddOns modal state (shown after adding an item that has addOns)
  const [showAddOns, setShowAddOns] = useState(false);
  const [pendingAddOns, setPendingAddOns] = useState<Step[]>([]);
  const [addOnItemName, setAddOnItemName] = useState("");
  // Track which add-on items were selected in the current session (resets each time sheet opens)
  const [addOnSelections, setAddOnSelections] = useState<Set<string>>(new Set());

  const selectedCategory = menuData[selectedCategoryIndex];

  // Current items: deepest level of nav stack, or category root
  const currentItems = useMemo(() => {
    if (navStack.length > 0) {
      return navStack[navStack.length - 1].items;
    }
    return selectedCategory?.items || [];
  }, [selectedCategory, navStack]);

  // Breadcrumb trail
  const breadcrumbs = useMemo(() => {
    const crumbs: string[] = [selectedCategory?.title || "Menu"];
    navStack.forEach((n) => crumbs.push(n.name));
    return crumbs;
  }, [selectedCategory, navStack]);

  const handleCategoryClick = (index: number) => {
    setSelectedCategoryIndex(index);
    setNavStack([]);
    setViewingItem(null);
  };

  const handleItemClick = (item: Item) => {
    if (item.outOfStock || item.itemBlock) return;

    // Has customization steps → show modal with interactive selection
    if (item.steps && item.steps.length > 0) {
      setViewingItem(item);
      // Deep copy steps so selections don't mutate the menu data
      setEditableSteps(JSON.parse(JSON.stringify(item.steps)));
      return;
    }

    // Has nested items → navigate deeper
    if (item.items && item.items.length > 0) {
      setNavStack((prev) => [...prev, { name: item.name || "", items: item.items }]);
      return;
    }

    // Leaf item — add directly to cart
    addDirectToCart(item);
  };

  const handleBack = () => {
    if (viewingItem) {
      setViewingItem(null);
      return;
    }
    if (navStack.length > 0) {
      setNavStack((prev) => prev.slice(0, -1));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (viewingItem) setViewingItem(null);
    if (index === 0) {
      setNavStack([]);
    } else {
      setNavStack((prev) => prev.slice(0, index));
    }
  };

  // Handle clicking a step item (interactive selection in modal)
  const handleStepItemClick = (stepIdx: number, itemIdx: number) => {
    setEditableSteps((prev) => {
      const updated = [...prev];
      const step = { ...updated[stepIdx], items: [...(updated[stepIdx].items || [])] };
      const clickedItem = step.items![itemIdx];

      if (step.maxSelect === 1 && !clickedItem.selected) {
        // Single-select: deselect all others, select this one
        step.items = step.items!.map((si, i) => ({
          ...si,
          selected: i === itemIdx,
          quantity: i === itemIdx ? (si.quantity || 1) : 1,
        }));
      } else {
        // Multi-select: toggle
        step.items![itemIdx] = {
          ...clickedItem,
          selected: !clickedItem.selected,
          quantity: !clickedItem.selected ? (clickedItem.quantity || 1) : clickedItem.quantity,
        };
      }

      updated[stepIdx] = step;
      return updated;
    });
  };

  // Handle quantity +/- on step items
  const handleStepItemQtyChange = (stepIdx: number, itemIdx: number, delta: number) => {
    setEditableSteps((prev) => {
      const updated = [...prev];
      const step = { ...updated[stepIdx], items: [...(updated[stepIdx].items || [])] };
      const si = { ...step.items![itemIdx] };
      const newQty = (si.quantity || 1) + delta;
      if (newQty < 1) {
        si.selected = false;
        si.quantity = 1;
      } else {
        si.quantity = Math.min(newQty, step.maxSelect || 99);
      }
      step.items![itemIdx] = si;
      updated[stepIdx] = step;
      return updated;
    });
  };

  // Cart totals
  const cartTotal = cartItems.reduce((sum, ci) => sum + ci.price * ci.quantity, 0);
  const cartCount = cartItems.reduce((sum, ci) => sum + ci.quantity, 0);

  // Validation error state for steps
  const [stepError, setStepError] = useState<string | null>(null);

  // Add item with customizations (from modal)
  const addCustomizedToCart = () => {
    if (!viewingItem) return;

    // Validate required steps
    for (let i = 0; i < editableSteps.length; i++) {
      const step = editableSteps[i];
      const isRequired = !!(step.minSelect && step.minSelect > 0);
      if (!isRequired) continue;

      const selectedCount = step.items?.reduce((sum, si) =>
        si.selected ? sum + (si.quantity || 1) : sum, 0) ?? 0;

      if (selectedCount < (step.minSelect ?? 0)) {
        // Mark the step with error and scroll to it
        setEditableSteps((prev) => {
          const updated = [...prev];
          updated[i] = { ...updated[i], error: true };
          return updated;
        });
        setStepError(step.name || "");
        // Clear error after 2 seconds
        setTimeout(() => {
          setStepError(null);
          setEditableSteps((prev) =>
            prev.map((s) => ({ ...s, error: false }))
          );
        }, 2000);
        return;
      }
    }

    // Validation passed — build cart item
    let totalPrice = viewingItem.price || 0;
    const customizations: string[] = [];

    for (const step of editableSteps) {
      for (const si of step.items || []) {
        if (si.selected) {
          totalPrice += (si.price || 0) * (si.quantity || 1);
          customizations.push(si.name || "");
        }
      }
    }

    setCartItems((prev) => [
      ...prev,
      {
        id: nextCartId(),
        name: viewingItem.name || "",
        price: totalPrice,
        quantity: 1,
        customizations,
        image: viewingItem.image || null,
      },
    ]);

    const itemAddOns = viewingItem.addOns;
    const itemName = viewingItem.name || "Item";

    setViewingItem(null);
    setEditableSteps([]);
    setStepError(null);

    // Show addOns modal if item has add-ons (e.g. "Make it a Combo?")
    if (itemAddOns && itemAddOns.length > 0) {
      setPendingAddOns(itemAddOns);
      setAddOnItemName(itemName);
      setAddOnSelections(new Set());
      setShowAddOns(true);
    } else {
      flashAdded(itemName);
    }
  };

  // Add leaf item directly (no steps)
  const addDirectToCart = (item: Item) => {
    setCartItems((prev) => [
      ...prev,
      {
        id: nextCartId(),
        name: item.name || "",
        price: item.price || 0,
        quantity: 1,
        customizations: [],
        image: item.image || null,
      },
    ]);

    if (item.addOns && item.addOns.length > 0) {
      setPendingAddOns(item.addOns);
      setAddOnItemName(item.name || "Item");
      setAddOnSelections(new Set());
      setShowAddOns(true);
    } else {
      flashAdded(item.name || "Item");
    }
  };

  // Remove from cart
  const removeFromCart = (id: string) => {
    setCartItems((prev) => prev.filter((ci) => ci.id !== id));
  };

  // Brief "Added!" flash
  const flashAdded = (name: string) => {
    setAddedFlash(name);
    setTimeout(() => setAddedFlash(null), 1500);
  };

  const canGoBack = navStack.length > 0 || viewingItem !== null;

  // Grid columns: 2 for ≤8 regular (non-banner) items, 3 for more
  const regularItemCount = currentItems.filter((i) => !i.bannerImage).length;
  const gridColumns = regularItemCount > 8 ? 3 : 2;

  // Render the item grid with banners breaking out of the flex grid
  const renderItems = () => {
    const elements: React.ReactNode[] = [];
    let gridBatch: Item[] = [];

    const flushGrid = () => {
      if (gridBatch.length === 0) return;
      const batch = [...gridBatch];
      const batchKey = batch.map((i) => i.id ?? i.name).join("-");
      elements.push(
        <div key={`grid-${batchKey}`} className={`sim-grid sim-grid-${gridColumns}col`}>
          {batch.map((item, idx) => renderCard(item, idx))}
        </div>
      );
      gridBatch = [];
    };

    currentItems.forEach((item, idx) => {
      if (item.bannerImage) {
        flushGrid();
        elements.push(
          <div
            key={`banner-${item.id ?? idx}`}
            className="sim-banner"
            onClick={() => handleItemClick(item)}
          >
            <img src={item.bannerImage} alt={item.name || ""} className="sim-banner-img" />
          </div>
        );
      } else {
        gridBatch.push(item);
      }
    });

    flushGrid();
    return elements;
  };

  const renderCard = (item: Item, idx: number) => {
    const hasChildren = item.items && item.items.length > 0;
    const hasSteps = item.steps && item.steps.length > 0;

    return (
      <button
        key={item.id ?? idx}
        className={`sim-card ${item.heroItem ? `sim-card-featured sim-card-featured-${item.heroBadgeStyle || 'orange'}` : ""} ${(item.outOfStock || item.itemBlock) ? "sim-card-disabled" : ""}`}
        onClick={() => handleItemClick(item)}
        disabled={!!(item.outOfStock || item.itemBlock)}
      >
        {item.heroItem && (
          <span className={`sim-featured-badge sim-badge-${item.heroBadgeStyle || 'orange'}`}>
            &#9733; {item.heroBadgeText || 'FEATURED'}
          </span>
        )}
        {(item.outOfStock || item.itemBlock) && <span className="sim-oos-badge">Unavailable</span>}

        <div className="sim-card-img-wrap">
          {item.image ? (
            <img src={item.image} alt="" className="sim-card-img" />
          ) : (
            <div className="sim-card-img-placeholder" />
          )}
        </div>

        <div className="sim-card-name">{item.name}</div>

        {item.highlights && item.highlights.length > 0 && (
          <div className="sim-card-highlights">
            {item.highlights.map((h, i) => (
              <span key={i} className="sim-highlight-chip">{h.name}</span>
            ))}
          </div>
        )}

        {(item.price && item.price > 0) || (item.calories && item.calories > 0) ? (
          <div className="sim-card-meta">
            {item.price && item.price > 0 ? (
              <span className="sim-card-price">${item.price.toFixed(2)}</span>
            ) : <span />}
            {item.calorieRange ? (
              <span className="sim-card-cal">{item.calorieRange} Cal</span>
            ) : item.calories && item.calories > 0 ? (
              <span className="sim-card-cal">{item.calories} Cal</span>
            ) : <span />}
          </div>
        ) : null}

        {hasChildren && !hasSteps && (
          <span className="sim-card-arrow">&rsaquo;</span>
        )}
      </button>
    );
  };

  return (
    <div className="sim-screen">
      {/* Header */}
      <div className="sim-header">
        <div className="sim-header-left">
          <span className="sim-logo-text">KIOSK PREVIEW</span>
        </div>
        <div className="sim-header-center">
        </div>
        <div className="sim-header-right">
          <div className="sim-cart-preview" onClick={() => setShowCart(true)} style={{ cursor: "pointer", position: "relative" }}>
            <div className="sim-cart-icon-section">
              <span className="sim-cart-icon">&#128722;</span>
            </div>
            <div className="sim-cart-divider" />
            <div className="sim-cart-price-section">
              <span className="sim-cart-total-label">TOTAL</span>
              <span className="sim-cart-label">${cartTotal.toFixed(2)}</span>
            </div>
            {cartCount > 0 && (
              <span className="sim-cart-badge">{cartCount}</span>
            )}
          </div>
        </div>
      </div>

      <div className="sim-body">
        {/* Sidebar */}
        <div className="sim-sidebar">
          {menuData.map((cat, idx) => (
            <button
              key={idx}
              className={`sim-sidebar-item ${idx === selectedCategoryIndex ? "sim-sidebar-active" : ""}`}
              onClick={() => handleCategoryClick(idx)}
            >
              {cat.image && <img src={cat.image} alt="" className="sim-sidebar-img" />}
              <span className="sim-sidebar-label">{cat.title}</span>
              {idx === selectedCategoryIndex && <div className="sim-sidebar-indicator" />}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div className="sim-content">
          {/* Breadcrumbs */}
          <div className="sim-breadcrumb">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="sim-breadcrumb-sep">&rsaquo;</span>}
                <span
                  className={idx === breadcrumbs.length - 1 ? "sim-breadcrumb-current" : "sim-breadcrumb-link"}
                  onClick={() => idx < breadcrumbs.length - 1 && handleBreadcrumbClick(idx)}
                >
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Items */}
          <div className="sim-content-scroll">
            {renderItems()}
          </div>
        </div>
      </div>

      {/* ── Customization Modal ── */}
      {viewingItem && (
        <div className="sim-modal-overlay" onClick={() => setViewingItem(null)}>
          <div className="sim-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sim-modal-header">
              {viewingItem.image && (
                <img src={viewingItem.image} alt="" className="sim-modal-img" />
              )}
              <div className="sim-modal-header-text">
                <div className="sim-modal-title">{viewingItem.name}</div>
                <div className="sim-modal-subtitle">
                  {viewingItem.price ? `$${viewingItem.price.toFixed(2)}` : ""}
                  {viewingItem.calories ? ` | ${viewingItem.calorieRange || viewingItem.calories} Cal` : ""}
                </div>
              </div>
              <button className="sim-modal-close" onClick={() => setViewingItem(null)}>&times;</button>
            </div>

            <div className="sim-modal-body">
              {editableSteps.map((step, stepIdx) => {
                const isRequired = !!(step.minSelect && step.minSelect > 0);
                const selectedCount = step.items?.reduce((sum, i) =>
                  i.selected ? sum + (i.quantity || 1) : sum, 0) ?? 0;
                const isSatisfied = !isRequired || selectedCount >= (step.minSelect ?? 0);
                const borderColor = step.error ? "red" : isSatisfied ? "#43a047" : "#FF8F00";

                // Build selection range label (matches kiosk logic)
                const min = step.minSelect ?? 0;
                const max = step.maxSelect ?? 0;
                let rangeLabel: string | null = null;
                if (min > 0 || max > 0) {
                  if (min > 0 && max > 0 && min === max) {
                    rangeLabel = `Select ${min}`;
                  } else if (min > 0 && max > 0) {
                    rangeLabel = `Select ${min}–${max}`;
                  } else if (min > 0) {
                    rangeLabel = `Select at least ${min}`;
                  } else {
                    rangeLabel = `Up to ${max}`;
                  }
                }

                return (
                  <div key={stepIdx} className="sim-step">
                    {/* Step header: name + required badge or checkmark */}
                    <div className="sim-step-header" style={{ borderBottomColor: borderColor }}>
                      <span className="sim-step-name">
                        {step.name}
                        {step.isOnSale && <span className="sim-onsale-badge">&#9733;</span>}
                      </span>
                      {step.error && (
                        <span className="sim-step-badge-error">
                          Required &mdash; {
                            min > 0 && max > 0 && min === max
                              ? `Select ${min}`
                              : min > 0 && max > 0
                                ? `Select ${min}–${max}`
                                : `Select at least ${min}`
                          }
                        </span>
                      )}
                      {!step.error && isRequired && !isSatisfied && (
                        <span className="sim-step-badge-required">Required</span>
                      )}
                      {!step.error && isRequired && isSatisfied && (
                        <span className="sim-step-check-header">&#10003;</span>
                      )}
                    </div>

                    {/* Selection range text below header (italic, separate line) */}
                    {rangeLabel && (
                      <div className="sim-step-range-line" style={{ color: isRequired && !isSatisfied ? "#E65100" : "gray" }}>
                        {rangeLabel}
                      </div>
                    )}

                    {/* Step items — clickable */}
                    <div className="sim-step-items">
                      {step.items?.map((si, siIdx) => (
                        <div
                          key={siIdx}
                          className={`sim-step-item ${si.selected ? "sim-step-item-selected" : ""} ${(si.outOfStock || si.itemBlock) ? "sim-step-item-disabled" : ""}`}
                          onClick={() => !(si.outOfStock || si.itemBlock) && handleStepItemClick(stepIdx, siIdx)}
                          style={{ cursor: (si.outOfStock || si.itemBlock) ? "not-allowed" : "pointer" }}
                        >
                          {si.selected && <span className="sim-step-check">&#10003;</span>}
                          {si.image && <img src={si.image} alt="" className="sim-step-item-img" />}
                          <div className="sim-step-item-name">{si.name}</div>
                          <div className="sim-step-item-meta">
                            {si.price && si.price > 0 ? (
                              <span className="sim-step-item-price">+${(si.price * (si.quantity || 1)).toFixed(2)}</span>
                            ) : null}
                            {si.calorieRange ? (
                              <span className="sim-step-item-cal">{si.calorieRange} Cal</span>
                            ) : si.calories && si.calories > 0 ? (
                              <span className="sim-step-item-cal">{si.calories} Cal</span>
                            ) : null}
                          </div>

                          {/* Quantity controls for selected items with quantity modifier */}
                          {si.selected && step.modifier === "quantity" && (
                            <div className="sim-qty-overlay">
                              <button className="sim-qty-btn" onClick={(e) => { e.stopPropagation(); handleStepItemQtyChange(stepIdx, siIdx, -1); }}>−</button>
                              <span className="sim-qty-value">{si.quantity || 1}</span>
                              <button className="sim-qty-btn" onClick={(e) => { e.stopPropagation(); handleStepItemQtyChange(stepIdx, siIdx, 1); }}>+</button>
                            </div>
                          )}

                          {/* Portion indicator for selected items with portion modifier */}
                          {si.selected && step.modifier === "portion" && (
                            <div className="sim-portion-overlay">
                              {si.portion || "Select Portion"}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

            </div>

            <div className="sim-modal-footer">
              <div className="sim-modal-add-btn" onClick={addCustomizedToCart} style={{ cursor: "pointer" }}>
                &#128722; Add to Cart &mdash; ${(() => {
                  let total = viewingItem.price || 0;
                  for (const step of editableSteps) {
                    for (const si of step.items || []) {
                      if (si.selected) total += (si.price || 0) * (si.quantity || 1);
                    }
                  }
                  return total.toFixed(2);
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── AddOns bottom sheet (slides up, matches kiosk) ── */}
      {showAddOns && pendingAddOns.length > 0 && (
        <div className="sim-addons-backdrop" onClick={() => setShowAddOns(false)}>
          <div className="sim-addons-sheet" onClick={(e) => e.stopPropagation()}>
            {/* Drag handle */}
            <div className="sim-addons-handle"><div className="sim-addons-handle-bar" /></div>

            <div className="sim-addons-scroll">
              {/* Celebration banner */}
              <div className="sim-addons-banner">
                <div className="sim-addons-banner-check">&#10003;</div>
                <span className="sim-addons-banner-name">{addOnItemName}</span>
                <span className="sim-addons-banner-label">Item Added!</span>
              </div>

              {/* Add-on groups */}
              {pendingAddOns.map((addon, addonIdx) => {
                const palettes = [
                  { grad: "linear-gradient(90deg, #1a237e, #3949ab)", tint: "#e8eaf6", border: "#7986cb" },
                  { grad: "linear-gradient(90deg, #E65100, #FF8F00)", tint: "#FFF8E1", border: "#FFB74D" },
                  { grad: "linear-gradient(90deg, #00695C, #00897B)", tint: "#E0F2F1", border: "#80CBC4" },
                  { grad: "linear-gradient(90deg, #6A1B9A, #AB47BC)", tint: "#F3E5F5", border: "#CE93D8" },
                ];
                const pal = palettes[addonIdx % palettes.length];

                return (
                  <div key={addonIdx} className="sim-addons-section" style={{ backgroundColor: pal.tint, borderColor: pal.border }}>
                    <div className="sim-addons-section-header" style={{ background: pal.grad }}>
                      <div className="sim-addons-header-line" />
                      <span className="sim-addons-section-title">{(addon.name || "ADD-ONS").toUpperCase()}</span>
                      <div className="sim-addons-header-line" />
                    </div>

                    <div className="sim-addons-grid">
                      {addon.items?.map((si, siIdx) => {
                        const key = `${addonIdx}-${siIdx}`;
                        const isSelected = addOnSelections.has(key);

                        return (
                          <div
                            key={siIdx}
                            className={`sim-addon-card ${isSelected ? "sim-addon-card-selected" : ""}`}
                            onClick={() => {
                              if (isSelected) {
                                // Remove from cart and deselect
                                setCartItems((prev) => {
                                  const idx = prev.findIndex((ci) => ci.name === si.name && ci.id.startsWith("cart-"));
                                  if (idx >= 0) return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
                                  return prev;
                                });
                                setAddOnSelections((prev) => { const next = new Set(prev); next.delete(key); return next; });
                              } else {
                                // Add to cart and select
                                setCartItems((prev) => [
                                  ...prev,
                                  { id: nextCartId(), name: si.name || "", price: si.price || 0, quantity: 1, customizations: [], image: si.image || null },
                                ]);
                                setAddOnSelections((prev) => new Set(prev).add(key));
                              }
                            }}
                          >
                            {isSelected && <span className="sim-addon-check">&#10003;</span>}

                            <div className="sim-addon-img-wrap">
                              {si.image && <img src={si.image} alt="" className="sim-addon-img" />}
                            </div>

                            <div className="sim-addon-name">{si.name}</div>

                            {si.highlights && si.highlights.length > 0 && (
                              <div className="sim-addon-highlight">
                                {si.highlights.map((h, i) => (
                                  <span key={i}>{h.name}</span>
                                ))}
                              </div>
                            )}

                            <div style={{ flex: 1 }} />

                            <div className="sim-addon-price-row">
                              {si.price && si.price > 0 ? (
                                <span className="sim-addon-price-pill">+${si.price.toFixed(2)}</span>
                              ) : null}
                              {(si.calorieRange || (si.calories && si.calories > 0)) ? (
                                <span className="sim-addon-cal">{si.calorieRange || si.calories} Cal</span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="sim-addons-footer">
              <div className="sim-addons-footer-buttons">
                <div className="sim-addons-continue-btn" onClick={() => setShowAddOns(false)}>
                  Continue Ordering
                </div>
                <div className="sim-addons-checkout-btn" onClick={() => { setShowAddOns(false); setShowCart(true); }}>
                  &#128722; Go to Checkout
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer — calorie advisory */}
      <div className="sim-footer">
        2,000 calories a day is used for general nutrition advice, but calorie needs vary.
        <br />
        Additional nutrition information available upon request.
      </div>

      {/* ── Cart panel (60% right drawer, matching kiosk) ── */}
      {showCart && (
        <div className="sim-cart-overlay" onClick={() => setShowCart(false)}>
          <div className="sim-cart-panel" onClick={(e) => e.stopPropagation()}>
            {/* Header — gradient banner */}
            <div className="sim-cart-banner">
              <span className="sim-cart-banner-icon">&#128722;</span>
              <span className="sim-cart-banner-title">Order Summary</span>
              <span className="sim-cart-banner-badge">{cartCount}</span>
              <button className="sim-cart-banner-close" onClick={() => setShowCart(false)}>&times;</button>
            </div>

            {/* Cart items */}
            <div className="sim-cart-body">
              {cartItems.length === 0 ? (
                <div className="sim-cart-empty">
                  <div className="sim-cart-empty-icon">&#128722;</div>
                  <div className="sim-cart-empty-text">Your cart is empty</div>
                  <div className="sim-cart-empty-sub">Add items from the menu to get started</div>
                </div>
              ) : (
                cartItems.map((ci) => (
                  <div key={ci.id} className="sim-cart-item-card">
                    {/* Delete button */}
                    <div className="sim-cart-item-actions">
                      <button className="sim-cart-item-delete" onClick={() => removeFromCart(ci.id)}>
                        &#128465;
                      </button>
                    </div>

                    {/* Image + details */}
                    <div className="sim-cart-item-top">
                      <div className="sim-cart-item-img-box">
                        {ci.image ? (
                          <img src={ci.image} alt="" className="sim-cart-item-img" />
                        ) : (
                          <div className="sim-cart-item-img-placeholder" />
                        )}
                      </div>
                      <div className="sim-cart-item-details">
                        <div className="sim-cart-item-name">{ci.name}</div>
                        {ci.customizations.length > 0 && (
                          <div className="sim-cart-item-customs">
                            {ci.customizations.map((c, i) => (
                              <span key={i} className="sim-cart-item-chip">
                                <span className="sim-cart-chip-check">&#10003;</span> {c}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Price + quantity */}
                    <div className="sim-cart-item-bottom">
                      <span className="sim-cart-item-price">${(ci.price * ci.quantity).toFixed(2)}</span>
                      <div className="sim-cart-item-qty">
                        <button
                          className="sim-cart-qty-btn sim-cart-qty-minus"
                          disabled={ci.quantity <= 1}
                          onClick={() => {
                            if (ci.quantity <= 1) return;
                            setCartItems((prev) =>
                              prev.map((item) => item.id === ci.id ? { ...item, quantity: item.quantity - 1 } : item)
                            );
                          }}
                        >&minus;</button>
                        <span className="sim-cart-qty-num">{ci.quantity}</span>
                        <button
                          className="sim-cart-qty-btn sim-cart-qty-plus"
                          onClick={() => {
                            setCartItems((prev) =>
                              prev.map((item) => item.id === ci.id ? { ...item, quantity: item.quantity + 1 } : item)
                            );
                          }}
                        >+</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer — totals + checkout */}
            {cartItems.length > 0 && (
              <div className="sim-cart-footer">
                <div className="sim-cart-total-row">
                  <span className="sim-cart-total-label">Subtotal</span>
                  <span className="sim-cart-total-value">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="sim-cart-total-row">
                  <span className="sim-cart-total-label">Tax</span>
                  <span className="sim-cart-total-value">${(cartTotal * 0.07).toFixed(2)}</span>
                </div>
                <div className="sim-cart-divider" />
                <div className="sim-cart-total-row sim-cart-grand-total">
                  <span>Total</span>
                  <span>${(cartTotal * 1.07).toFixed(2)}</span>
                </div>
                <div className="sim-cart-checkout-btn">
                  &#128722; Checkout &mdash; ${(cartTotal * 1.07).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── "Added!" flash ── */}
      {addedFlash && (
        <div className="sim-added-flash">
          &#10003; {addedFlash} added!
        </div>
      )}
    </div>
  );
};

export default KioskSimulator;
